import React, {useState, useMemo, useEffect} from 'react';
import {Row, Col, Breadcrumb, Tabs, Image, Alert, Spin, Empty} from 'antd';
import {
    HomeOutlined,
    ShoppingCartOutlined,
    MinusOutlined,
    PlusOutlined,
    AppstoreOutlined,
    MedicineBoxOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import {useNavigate, useParams} from 'react-router-dom';
import {useCart} from '../contexts/ShopContext';
import Button from '../components/data_entry/Button';
import ProductCard from '../components/shop/ProductCard';
import useDefaultRequest from '../hooks/useDefaultRequest';
import ENDPOINTS from '../constants/endpoints';
import {
    PLANTEI_DARK,
    WHITE,
    LIGHT_GRAY,
    BORDER_GRAY,
    GREE_1,
    GREE_3
} from '../utils/colors';
import imagePlaceholder from '../assets/imgs/image-placeholder.jpg';
import {moneyMask} from '../utils/utils';

// ========================
// STYLED COMPONENTS
// ========================

const BreadcrumbWrapper = styled.div`
    margin-bottom: 24px;

    .ant-breadcrumb a {
        color: ${GREE_1};
    }

    .ant-breadcrumb a:hover {
        color: ${GREE_3};
    }
`;

const ProductSection = styled.div`
    background: ${WHITE};
    border-radius: 10px;
    padding: 32px;
    margin-bottom: 24px;
    border: 1px solid ${BORDER_GRAY};

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

const MainImageWrapper = styled.div`
    background: ${LIGHT_GRAY};
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;

    .ant-image {
        width: 100%;
        height: 100%;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ProductTitle = styled.h2`
    font-size: 26px;
    font-weight: 700;
    color: ${PLANTEI_DARK};
    margin: 0 0 12px 0;

    @media (max-width: 768px) {
        font-size: 22px;
    }
`;

const ProductCategory = styled.p`
    font-size: 14px;
    color: #999;
    margin-bottom: 16px;

    span {
        color: ${GREE_1};
        font-weight: 500;
    }
`;

const PriceWrapper = styled.div`
    margin-bottom: 20px;
`;

const CurrentPrice = styled.h3`
    font-size: 28px;
    font-weight: 700;
    color: ${GREE_1};
    margin: 0;
`;

const ProductDescription = styled.div`
    font-size: 15px;
    color: #555;
    line-height: 1.7;
    margin-bottom: 24px;
`;

const QuantityWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0;
    margin-bottom: 24px;
    width: 130px;
`;

const QtyButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid ${BORDER_GRAY};
    background: ${LIGHT_GRAY};
    color: ${PLANTEI_DARK};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: ${GREE_1};
        color: ${WHITE};
        border-color: ${GREE_1};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const QtyInput = styled.input`
    width: 50px;
    height: 36px;
    text-align: center;
    border: none;
    font-size: 16px;
    font-weight: 600;
    color: ${PLANTEI_DARK};
    background: transparent;
    outline: none;
`;

const AddToCartButton = styled(Button)`
    border-radius: 50px !important;
    height: 48px;
    padding: 0 32px;
    font-size: 16px;
    font-weight: 600;
    background: ${GREE_1} !important;
    border-color: ${GREE_1} !important;
    color: ${WHITE} !important;

    &:hover {
        background: ${GREE_3} !important;
        border-color: ${GREE_3} !important;
    }
`;

const DescriptionSection = styled.div`
    background: ${WHITE};
    border-radius: 10px;
    padding: 32px;
    margin-bottom: 24px;
    border: 1px solid ${BORDER_GRAY};

    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${GREE_1};
    }

    .ant-tabs-ink-bar {
        background: ${GREE_1};
    }

    .ant-tabs-tab:hover .ant-tabs-tab-btn {
        color: ${GREE_3};
    }

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

const DescriptionContent = styled.div`
    font-size: 14px;
    color: #555;
    line-height: 1.8;

    p {
        margin-bottom: 12px;
    }

    b {
        color: ${PLANTEI_DARK};
    }
`;

const RelatedTitle = styled.h3`
    font-size: 22px;
    font-weight: 700;
    color: ${PLANTEI_DARK};
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid ${GREE_1};
    display: inline-block;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
`;

// ========================
// HELPERS
// ========================


// ========================
// COMPONENT
// ========================

export default function ShopProductDetail() {
    const navigate = useNavigate();
    const {slug} = useParams();
    const {addToCart} = useCart();

    const [quantity, setQuantity] = useState(1);

    // Fetch product detail
    const {data: product, isLoading} = useDefaultRequest({
        url: ENDPOINTS.ECOMMERCE_PRODUCT_PUBLIC_DETAIL.replace(':slug', slug),
        dataDefault: null,
        dependence: [slug],
    });

    // Fetch related products (same category)
    const {data: relatedProducts, isLoading: loadingRelated} = useDefaultRequest({
        url: ENDPOINTS.ECOMMERCE_PRODUCT_PUBLIC,
        params: {
            category: product?.category,
            page_size: 4
        },
        dataDefault: [],
        hasPagination: true,
        makeRequest: !!product?.category,
        dependence: [product?.category],
    });

    const filteredRelated = useMemo(() => {
        if (!product) return [];
        return relatedProducts.filter(p => p.id !== product.id).slice(0, 4);
    }, [relatedProducts, product]);

    const handleAddToCart = () => {
        addToCart(product, quantity);
    };

    const prescriptionLocked = product?.requires_prescription && product?.can_add_to_cart;

    useEffect(() => {
        if (prescriptionLocked && product?.available_quantity) {
            setQuantity(product.available_quantity);
        }
    }, [prescriptionLocked, product?.available_quantity]);

    const handleQuantityChange = (delta) => {
        setQuantity(prev => {
            const next = prev + delta;
            if (next < 1) return 1;
            if (product.available_quantity && next > product.available_quantity) return product.available_quantity;
            return next;
        });
    };

    const handleDetail = (p) => {
        navigate(`/area-do-associado/loja/produtos/${p.slug}`);
        setQuantity(1);
    };

    if (isLoading) {
        return (
            <LoadingWrapper>
                <Spin size="large"/>
            </LoadingWrapper>
        );
    }

    if (!product) {
        return <Empty description="Produto não encontrado"/>;
    }

    const tabItems = [
        {
            key: 'description',
            label: 'Descrição',
            children: (
                <DescriptionContent>
                    {product.description}
                </DescriptionContent>
            )
        },
    ];

    return (
        <div>
            {/* Breadcrumb */}
            <BreadcrumbWrapper>
                <Breadcrumb
                    items={[
                        {
                            title: (
                                <a onClick={() => navigate('/area-do-associado/loja/produtos')}>
                                    <HomeOutlined/> Início
                                </a>
                            ),
                        },
                        {
                            title: (
                                <a onClick={() => navigate('/area-do-associado/loja/produtos')}>
                                    <AppstoreOutlined/> Produtos
                                </a>
                            ),
                        },
                        {
                            title: product.name,
                        },
                    ]}
                />
            </BreadcrumbWrapper>

            {/* Product Main Section */}
            <ProductSection>
                <Row gutter={[32, 32]}>
                    {/* Image */}
                    <Col xs={24} lg={12}>
                        <MainImageWrapper>
                            <Image
                                src={product.image || imagePlaceholder}
                                alt={product.name}
                                preview
                                fallback={imagePlaceholder}
                            />
                        </MainImageWrapper>
                    </Col>

                    {/* Product Info */}
                    <Col xs={24} lg={12}>
                        <ProductTitle>{product.name}</ProductTitle>

                        <ProductCategory>
                            Categoria: <span>{product.category_name}</span>
                        </ProductCategory>

                        <PriceWrapper>
                            <CurrentPrice>R$ {moneyMask(product.price)}</CurrentPrice>
                        </PriceWrapper>

                        {/*{product.available_quantity !== null && (*/}
                        {/*    <div style={{fontSize: 14, color: '#666', marginBottom: 20}}>*/}
                        {/*        <strong style={{color: GREE_1}}>{product.available_quantity} unidades em estoque</strong>*/}
                        {/*    </div>*/}
                        {/*)}*/}

                        <ProductDescription>
                            {product.description}
                        </ProductDescription>

                        {/* Prescription Warning */}
                        {!product.can_add_to_cart && product.requires_prescription && (
                            <Alert
                                message="Prescrição médica necessária"
                                description="Este produto requer uma prescrição médica válida para ser adicionado ao carrinho. Por favor, envie sua prescrição ou entre em contato conosco para mais informações."
                                type="warning"
                                showIcon
                                icon={<MedicineBoxOutlined/>}
                                style={{marginBottom: 24}}
                            />
                        )}
                        {product.can_add_to_cart && product.requires_prescription && (
                            <Alert
                                message="Prescrição ativa"
                                description={`Este produto requer prescrição médica. Você possui uma prescrição válida e pode adicioná-lo ao carrinho. Quantidade disponível para a receita: ${product.available_quantity} unidade(s).`}
                                type="success"
                                showIcon
                                icon={<MedicineBoxOutlined/>}
                                style={{marginBottom: 24}}
                            />
                        )}

                        {/* Quantity Selector & Add to Cart */}
                        {product.can_add_to_cart && (
                            <>
                                <QuantityWrapper>
                                    <QtyButton disabled={prescriptionLocked} onClick={() => handleQuantityChange(-1)}>
                                        <MinusOutlined/>
                                    </QtyButton>
                                    <QtyInput
                                        type="text"
                                        value={quantity}
                                        readOnly
                                    />
                                    <QtyButton disabled={prescriptionLocked} onClick={() => handleQuantityChange(1)}>
                                        <PlusOutlined/>
                                    </QtyButton>
                                </QuantityWrapper>

                                {quantity > 1 && (
                                    <div style={{marginBottom: 16, fontSize: 18, color: '#666'}}>
                                        Subtotal: <strong style={{color: GREE_1}}>R$ {moneyMask(quantity * product.price)}</strong>
                                    </div>
                                )}

                                <AddToCartButton
                                    icon={<ShoppingCartOutlined/>}
                                    onClick={handleAddToCart}
                                >
                                    Adicionar ao carrinho
                                </AddToCartButton>
                            </>
                        )}
                    </Col>
                </Row>
            </ProductSection>

            {/* Description Tabs */}
            <DescriptionSection>
                <Tabs items={tabItems}/>
            </DescriptionSection>

            {/* Related Products */}
            {filteredRelated.length > 0 && (
                <div>
                    <RelatedTitle>Produtos Relacionados</RelatedTitle>
                    <Row gutter={[16, 16]}>
                        {filteredRelated.map(p => (
                            <Col key={p.id} xs={24} sm={12} lg={6}>
                                <ProductCard
                                    product={p}
                                    onDetail={handleDetail}

                                />
                            </Col>
                        ))}
                    </Row>
                </div>
            )}
        </div>
    );
}